import {
  Component,
  ElementRef,
  ChangeDetectorRef,
  HostListener,
} from '@angular/core';
import { cloneDeep } from 'lodash';

import { environment } from 'src/environments/environment';

import { ModalService } from 'src/app/components/modals/modal.service';

import { AttributeDataService } from '../../services/attribute-data.service';
import { TemplateEditorUtilsService } from '../../services/template-editor-utils.service';
import { GoogleCalendarService } from '../services/google-calendar.service';
import { MicrosoftOutlookService } from '../services/microsoft-outlook.service';
import { OAuthService } from 'src/app/components/content-oauth/services/oauth.service';
import { TemplateEditorService } from '../../services/template-editor.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';

import { ComponentsService } from '../../services/components.service';
import { CalendarBase } from '../calendar-base/calendar.base';

@Component({
  selector: 'template-component-layout-calendar',
  templateUrl: './layout-calendar.component.html',
  styleUrls: ['./layout-calendar.component.scss'],
  standalone: false,
})
export class LayoutCalendarComponent extends CalendarBase {
  private static readonly DEFAULT_LAYOUT_ID =
    ComponentsService.LAYOUTS_MAP['rise-layout-calendar'].SCROLLING;

  private static readonly DEFAULT_SLIDE_DURATION = 10;

  override multiCalendarComponent = true;

  calendarPickerGoogleOpen = false;
  calendarPickerOutlookOpen = false;

  readonly LayoutType = ComponentsService.LAYOUTS_MAP['rise-layout-calendar'];

  public componentId: string;
  public layout: string;
  /** Seconds; shown for Single Event layout only. Persisted always; reset to default when layout is Scrolling. */
  public slideDuration = LayoutCalendarComponent.DEFAULT_SLIDE_DURATION;

  private componentLayout: Record<string, unknown>;
  private componentServices: Record<string, unknown>[];

  constructor(
    private elementRef: ElementRef,
    changeDetectorRef: ChangeDetectorRef,
    modalService: ModalService,
    private attributeDataService: AttributeDataService,
    private templateEditorUtils: TemplateEditorUtilsService,
    private componentsFactory: ComponentsService,
    templateEditorService: TemplateEditorService,
    userStateService: UserStateService,
    googleCalendarService: GoogleCalendarService,
    microsoftOutlookService: MicrosoftOutlookService,
    authService: OAuthService,
  ) {
    super(
      changeDetectorRef,
      googleCalendarService,
      microsoftOutlookService,
      authService,
      userStateService,
      modalService,
      templateEditorService,
    );
    this.componentsFactory.registerDirective({
      type: 'rise-layout-calendar',
      element: this.elementRef.nativeElement,
      show: async (options?) => {
        this.spinner = false;
        this.componentId = this.componentsFactory.selected.id;
        this.load();
        this.userAccount = this.authService.getUserIdentifier(null);
        if (this.isPublicUrl()) {
          this.validateCalendarUrl();
        } else {
          await this.validateCredentials();
        }
      },
    });
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange() {
    if (this._directiveIsVisible() && !document.hidden && this.isPublicUrl()) {
      void this.validateCalendarUrl();
    }
  }

  private _directiveIsVisible() {
    return (
      this.componentsFactory.selected &&
      this.componentsFactory.selected.type === 'rise-layout-calendar'
    );
  }

  load() {
    this.reset();

    const defaultAttributes =
      this.componentsFactory.getSelectedDirective()?.defaultAttributes || {};
    const defaultLayout = cloneDeep(defaultAttributes?.layout || {});
    const defaultServices = cloneDeep(defaultAttributes?.services || []);

    const rawLayout = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'layout',
    );
    const rawServices = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'services',
    );

    this.componentLayout = this.templateEditorUtils.safeParseJson(
      rawLayout,
      defaultLayout,
    ) as Record<string, unknown>;
    this.componentServices = this.templateEditorUtils.safeParseJson(
      rawServices,
      defaultServices,
    ) as Record<string, unknown>[];

    const layoutAttributes =
      (this.componentLayout?.attributes as Record<string, unknown>) || {};
    const serviceAttributes =
      Array.isArray(this.componentServices) && this.componentServices.length > 0
        ? ((this.componentServices[0] as Record<string, unknown>)
            ?.attributes as Record<string, unknown>) || {}
        : {};

    this.calendarUrlStatus = null;
    this.provider = (serviceAttributes.provider as string | null) ?? null;
    this.componentAccount = (serviceAttributes.account as string) ?? '';
    this.source = (serviceAttributes.source as string) ?? '';

    this.layout =
      (this.componentLayout.layoutId as string) ||
      LayoutCalendarComponent.DEFAULT_LAYOUT_ID;

    this.range =
      this.templateEditorUtils.intValueFor(
        serviceAttributes.range,
        this.DEFAULT_RANGE,
      ) ?? this.DEFAULT_RANGE;
    this.period = ((serviceAttributes.period as string) ??
      this.DEFAULT_PERIOD) as 'days' | 'weeks' | 'months';
    const hideCompleted =
      serviceAttributes.completed !== undefined
        ? serviceAttributes.completed
        : false;
    this.showCompleted = !hideCompleted;

    this.showDescription =
      layoutAttributes.showDescription !== undefined
        ? !!layoutAttributes.showDescription
        : true;
    this.showLocation =
      layoutAttributes.showLocation !== undefined
        ? !!layoutAttributes.showLocation
        : true;
    this.color =
      (layoutAttributes.color as string) ?? this.DEFAULT_DISPLAY_COLOR;
    this.backgroundColor =
      (layoutAttributes.backgroundColor as string) ??
      this.DEFAULT_DISPLAY_BACKGROUND_COLOR;

    if (this.layout === this.LayoutType.SCROLLING) {
      this.slideDuration = LayoutCalendarComponent.DEFAULT_SLIDE_DURATION;
    } else {
      this.slideDuration = this._normalizeSlideDurationSeconds(
        layoutAttributes.slideDuration,
      );
    }

    const rawCalendars = serviceAttributes.calendars;
    if (rawCalendars != null) {
      this.selectedCalendars = this.parseCalendarsAttribute(rawCalendars);
    } else {
      this.selectedCalendars = [];
    }

    this.calendarType = (serviceAttributes.calendarType as string) || null;

    this.updateValidRange();

    const env = this.attributeDataService.getAttributeData(
      this.componentId,
      'environment',
    );
    if (!env) {
      this.save();
    }
  }

  async validateCalendarUrl(): Promise<boolean> {
    if (!this.source) {
      return true;
    }

    this.spinner = true;
    let isValid = false;

    try {
      const provider = this.getProviderFromUrl(this.source);
      switch (provider) {
        case this.PROVIDER_GOOGLE_CALENDAR: {
          const result = await this.googleCalendarService.validate(this.source);
          this.calendarUrlStatus = result; // preserves 'VALID' | 'INVALID' | 'FAILED'
          isValid = result === 'VALID';
          break;
        }
        case this.PROVIDER_ICAL:
          isValid = this.validateIcalUrl();
          this.calendarUrlStatus = isValid ? 'VALID' : 'INVALID';
          break;
        default:
          isValid = false;
          this.calendarUrlStatus = 'INVALID';
      }

      if (isValid) {
        this.provider = provider;
      }
    } finally {
      this.spinner = false;
    }

    this.changeDetectorRef.detectChanges();

    return isValid;
  }

  setLayout(layoutId: string) {
    this.layout = layoutId;
    if (layoutId === this.LayoutType.SCROLLING) {
      this.slideDuration = LayoutCalendarComponent.DEFAULT_SLIDE_DURATION;
    }
    this.save();
  }

  saveSlideDuration() {
    this.save();
  }

  protected override save() {
    if (!this.componentLayout) {
      this.componentLayout = {};
    }
    const previousServiceAttrs = ((
      this.componentServices?.[0] as Record<string, unknown> | undefined
    )?.attributes ?? {}) as Record<string, unknown>;
    const previousSource = (previousServiceAttrs.source as string) ?? '';
    let sourceToSave = this.source;
    if (this.isPublicUrl() && this.calendarUrlStatus === 'INVALID') {
      sourceToSave = previousSource;
    }

    let rangeToSave = this.templateEditorUtils.intValueFor(
      this.range,
      this.DEFAULT_RANGE,
    );
    let periodToSave = this.period;
    if (!this.isValidRange()) {
      rangeToSave = this.templateEditorUtils.intValueFor(
        previousServiceAttrs.range,
        this.DEFAULT_RANGE,
      ) as number;
      periodToSave = ((previousServiceAttrs.period as string) ??
        this.DEFAULT_PERIOD) as 'days' | 'weeks' | 'months';
    }

    const accountToSave = this._getAccountToSave();

    const slideDurationToSave =
      this.layout === this.LayoutType.SINGLE
        ? this._normalizeSlideDurationSeconds(this.slideDuration)
        : LayoutCalendarComponent.DEFAULT_SLIDE_DURATION;

    if (this.layout === this.LayoutType.SINGLE) {
      this.slideDuration = slideDurationToSave;
    }

    this.componentLayout.layoutId = this.layout;
    this.componentLayout.attributes = {
      showDescription: this.showDescription,
      showLocation: this.showLocation,
      color: this.color,
      backgroundColor: this.backgroundColor,
      slideDuration: slideDurationToSave,
    };

    if (
      !Array.isArray(this.componentServices) ||
      this.componentServices.length === 0
    ) {
      this.componentServices = [{}];
    }
    const firstService = this.componentServices[0] as Record<string, unknown>;
    if (!firstService.type) {
      firstService.type = 'component';
    }
    if (!firstService.source) {
      firstService.source = 'rise-data-calendar';
    }

    firstService.attributes = {
      provider: this.provider,
      calendarType: this.calendarType,
      account: accountToSave,
      source: sourceToSave,
      calendars: this.selectedCalendars,
      range: rangeToSave,
      period: periodToSave,
      completed: !this.showCompleted,
    };

    this.attributeDataService.setAttributeData(
      this.componentId,
      'layout',
      this.componentLayout,
    );
    this.attributeDataService.setAttributeData(
      this.componentId,
      'services',
      this.componentServices,
    );
    this.attributeDataService.setAttributeData(
      this.componentId,
      'environment',
      environment.production ? 'prod' : 'test',
    );
  }

  private _getAccountToSave(): string {
    const oauth = this.USER_AUTHORIZED_PROVIDERS as readonly string[];
    if (this.provider && oauth.includes(this.provider) && !this.isPublicUrl()) {
      return this.componentAccount ?? '';
    }
    return '';
  }

  /**
   * Coerces to a whole number of seconds and enforces a minimum of 1 (invalid / non-positive → at least 1).
   */
  private _normalizeSlideDurationSeconds(raw: unknown): number {
    const coerced =
      this.templateEditorUtils.intValueFor(
        raw,
        LayoutCalendarComponent.DEFAULT_SLIDE_DURATION,
      ) ?? LayoutCalendarComponent.DEFAULT_SLIDE_DURATION;
    return Math.max(1, coerced);
  }

  protected override onOutlookSingleCalendarAutoSelected(): void {}

  override reset() {
    super.reset();
    this.showCompleted = true;
    this.layout = LayoutCalendarComponent.DEFAULT_LAYOUT_ID;
    this.slideDuration = LayoutCalendarComponent.DEFAULT_SLIDE_DURATION;

    this.updateValidRange();
  }

  async selectCalendarType(calendarType: string) {
    this.reset();

    this.calendarType = calendarType;

    if (calendarType !== this.CALENDAR_TYPE_PUBLIC) {
      this.provider = calendarType;
    }

    const oauth = this.USER_AUTHORIZED_PROVIDERS as readonly string[];
    if (oauth.includes(calendarType)) {
      await this.connectAccount();
      if (this.authenticateFailed || !this.componentAccount) {
        this.calendarType = null;
        this.provider = null;
      }
    }

    this.save();
    this.changeDetectorRef.detectChanges();
  }

  get revokeFailedProviderName(): string {
    switch (this.revokeFailedProvider) {
      case this.PROVIDER_GOOGLE_CALENDAR:
        return 'Google';
      case this.PROVIDER_OUTLOOK_CALENDAR:
        return 'Microsoft';
      default:
        return '';
    }
  }

  onGoogleCalendarDropdownOpenChange(open: boolean): void {
    this.calendarPickerGoogleOpen = open;
    if (open) {
      this.calendarPickerOutlookOpen = false;
    }
  }

  onOutlookCalendarDropdownOpenChange(open: boolean): void {
    this.calendarPickerOutlookOpen = open;
    if (open) {
      this.calendarPickerGoogleOpen = false;
    }
  }

  _getSourceTypeName(provider) {
    switch (provider) {
      case this.PROVIDER_GOOGLE_CALENDAR:
        if (this.isPublicUrl()) {
          return 'Public';
        }

        return 'Google';
      case this.PROVIDER_OUTLOOK_CALENDAR:
        return 'Microsoft Outlook';
      default:
        return 'Public';
    }
  }
}
